import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: true,
    marketing: true
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('artcardo_cookie_consent');
    if (!consent) {
      // Show banner after 1 second delay for better UX
      setTimeout(() => setShowBanner(true), 1000);
    }
  }, []);

  const handleAcceptAll = () => {
    const consentData = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('artcardo_cookie_consent', JSON.stringify(consentData));
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const consentData = {
      necessary: true, // Necessary cookies cannot be rejected
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('artcardo_cookie_consent', JSON.stringify(consentData));
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    const consentData = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem('artcardo_cookie_consent', JSON.stringify(consentData));
    setShowBanner(false);
    setShowCustomize(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center pointer-events-none">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/50 pointer-events-auto" onClick={() => !showCustomize && setShowBanner(false)} />
      
      {/* Banner */}
      <div className="relative w-full max-w-6xl mx-4 mb-4 pointer-events-auto animate-slide-up">
        <div className="bg-white rounded-lg shadow-2xl border-2 border-gray-200">
          {!showCustomize ? (
            // Main Banner
            <div className="p-6 sm:p-8">
              <div className="flex items-start gap-4">
                {/* Icon */}
                <div className="flex-shrink-0 mt-1">
                  <Cookie className="w-8 h-8 text-red-600" />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    We Value Your Privacy
                  </h3>
                  <p className="text-sm text-gray-700 leading-relaxed mb-4">
                    <strong>ARTCARDO</strong> uses cookies and similar technologies to improve your experience on ARTCARDO, analyze site traffic, and personalize content. By clicking "Accept All", you consent to our use of cookies. You can customize your preferences or learn more in our{' '}
                    <Link to="/privacy-policy" className="text-red-600 underline hover:text-red-700 font-medium">
                      Privacy Policy
                    </Link>.
                  </p>

                  {/* Buttons */}
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={handleAcceptAll}
                      className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 font-semibold"
                    >
                      Accept All Cookies
                    </Button>
                    <Button
                      onClick={handleRejectAll}
                      variant="outline"
                      className="border-gray-300 hover:bg-gray-50 px-6 py-2.5"
                    >
                      Reject All
                    </Button>
                    <Button
                      onClick={() => setShowCustomize(true)}
                      variant="outline"
                      className="border-gray-300 hover:bg-gray-50 px-6 py-2.5"
                    >
                      Customize Preferences
                    </Button>
                  </div>

                  {/* Compliance Note */}
                  <p className="text-xs text-gray-500 mt-4">
                    This website complies with the Digital Personal Data Protection Act, 2023 (India) and GDPR (EU).
                  </p>
                </div>

                {/* Close button */}
                <button
                  onClick={() => setShowBanner(false)}
                  className="flex-shrink-0 text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Close"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
          ) : (
            // Customize Panel
            <div className="p-6 sm:p-8">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">
                    Cookie Preferences
                  </h3>
                  <p className="text-sm text-gray-600">
                    Choose which cookies you want to accept. Necessary cookies cannot be disabled.
                  </p>
                </div>
                <button
                  onClick={() => setShowCustomize(false)}
                  className="text-gray-400 hover:text-gray-600"
                  aria-label="Back"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="mt-1 w-5 h-5 rounded"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Necessary Cookies <span className="text-red-600 text-xs">(Required)</span>
                    </h4>
                    <p className="text-sm text-gray-600">
                      Essential for website functionality, authentication, security, and basic features. These cannot be disabled.
                    </p>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-200">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="mt-1 w-5 h-5 rounded cursor-pointer"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Analytics Cookies
                    </h4>
                    <p className="text-sm text-gray-600">
                      Help us understand how visitors interact with our website by collecting anonymous data to improve performance.
                    </p>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start gap-3 p-4 bg-white rounded-lg border border-gray-200">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="mt-1 w-5 h-5 rounded cursor-pointer"
                  />
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900 mb-1">
                      Marketing Cookies
                    </h4>
                    <p className="text-sm text-gray-600">
                      Used to deliver personalized advertisements and track campaign effectiveness across the web.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                <Button
                  onClick={handleSavePreferences}
                  className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 font-semibold"
                >
                  Save Preferences
                </Button>
                <Button
                  onClick={() => setShowCustomize(false)}
                  variant="outline"
                  className="border-gray-300 hover:bg-gray-50 px-6 py-2.5"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* CSS Animation */}
      <style jsx>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default CookieConsent;
